/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { UsageData, PricingTier } from '@/types/database'

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000'

/**
 * Fetch usage data from backend API (requires auth token, computes counts server-side)
 */
export function useUsage() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['usage', user?.id],
    queryFn: async (): Promise<UsageData> => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not authenticated')

      const response = await fetch(`${BACKEND_URL}/api/usage`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch usage: ${response.status}`)
      }

      return response.json()
    },
    enabled: !!user?.id,
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: true,
  })
}

/**
 * Read pricing tiers directly from Supabase (RLS allows SELECT for authenticated users)
 */
export function usePricingTiers() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['pricing-tiers'],
    queryFn: async (): Promise<PricingTier[]> => {
      const { data, error } = await supabase
        .from('pricing_tiers')
        .select('*')
        .order('sort_order', { ascending: true })

      if (error) throw error
      return data as PricingTier[]
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Helper: get the most urgent usage level across all resources
 */
export function getUrgentLevel(usage: UsageData | undefined): 'ok' | 'warning' | 'critical' | 'blocked' {
  if (!usage) return 'ok'

  const levels = [
    usage.usage.projects.level,
    usage.usage.total_clips.level,
    ...usage.per_project.map(p => p.commits.level),
  ]

  if (levels.includes('blocked')) return 'blocked'
  if (levels.includes('critical')) return 'critical'
  if (levels.includes('warning')) return 'warning'
  return 'ok'
}
